import {
  DeleteButton,
  EditButton,
  ListButton,
  RefreshButton,
  Show,
  useModal,
} from "@refinedev/antd";

import { Button, Space, Tag, Row, Col } from "antd";
import { IResourceComponentsProps, useNavigation, useShow } from "@refinedev/core";
import {
  GlobalOutlined,
  AppstoreOutlined,
  SafetyCertificateOutlined,
  LinkOutlined,
} from "@ant-design/icons";

import dayjs from "dayjs";
import { IMetadataAvcs } from "interfaces";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
} from "components/show";
import { formatEnrollmentDate } from "components/metadata/enrollmentDate";

export const MetadataAvcsShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IMetadataAvcs>();
  const record = result;

  const { list } = useNavigation();

  const {
    modalProps: modalPropsSubscription,
    show: showSubscription,
    close: closeSubscription,
  } = useModal();

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        title: "Show AVCS Service",
        subTitle: "active AVCS service",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <ListButton resource="metadata-avcs">AVCS Service</ListButton>
            <EditButton resource="metadata-avcs" recordItemId={record?.id} />
            <DeleteButton
              resource="metadata-avcs"
              recordItemId={record?.id}
              onSuccess={() => list("metadata-avcs")}
            />
            <RefreshButton resource="metadata-avcs" recordItemId={record?.id} />
          </Space>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Space>
          <Button type="default" onClick={showSubscription}>
            Associated Subscription
          </Button>
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Service Overview */}
        <ShowSection
          title="AVCS Service"
          subtitle="Admiralty Vector Chart Service configuration"
          icon={<GlobalOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="UKHO Licence" value={record?.subscription?.licence?.ukhoLicenceId} type="code" copyable />
            <ShowField label="Product Name" value={record?.productName} type="tag" tagColor="blue" />
            <ShowField label="Product Group" value={record?.productGroup} />
            <ShowField label="Licence Type" value={record?.licenceType} type="status" />
            <ShowField label="Activation Status" value={record?.activationStatus} type="status" />
            <ShowField label="Enrollment Date" value={formatEnrollmentDate(record?.enrollmentDate)} />
            <ShowField label="Expiry Date" value={record?.expiryDate} type="date" />
          </ShowFieldGrid>
        </ShowSection>

        <Row gutter={24}>
          {/* Subscription Details */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Subscription Details"
              subtitle="Billing and pricing information"
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField label="Currency" value={record?.currency} type="tag" tagColor="purple" />
                <ShowField label="Subscription Type" value={record?.subscriptionType} />
                <ShowField label="Payment Frequency" value={record?.paymentFrequency} />
                <ShowField label="Discount Percentage" value={record?.discountPercentage} type="percentage" />
                <ShowField label="Annual Flat Fee Per Vessel" value={record?.annualFlatFeePerVessel} type="currency" currency={record?.currency} />
              </ShowFieldGrid>
            </ShowSection>
          </Col>

          {/* URLs */}
          <Col xs={24} lg={12}>
            <ShowSection
              title="Documents"
              subtitle="Certificate and schedule links"
              icon={<LinkOutlined />}
              variant="default"
            >
              <ShowFieldGrid columns={1}>
                <ShowField label="Certificate URL" value={record?.certificateUrl} type="url" />
                <ShowField label="Schedule-A URL" value={record?.scheduleAUrl} type="url" />
              </ShowFieldGrid>
            </ShowSection>
          </Col>
        </Row>

        {/* Permit Configuration */}
        <ShowSection
          title="Permit Configuration"
          subtitle="Master and backup permit details"
          icon={<SafetyCertificateOutlined />}
          variant="subtle"
        >
          <Row gutter={24}>
            <Col xs={24} lg={12}>
              <ShowFieldGrid columns={1}>
                <ShowField label="Master Permit" value={record?.masterPermit} type="code" copyable />
                <ShowField label="Master Permit Version" value={record?.masterPermitVersion} />
                <ShowField label="Backup Permit" value={record?.backupPermit} type="code" copyable />
                <ShowField label="Backup Permit Version" value={record?.backupPermitVersion} />
              </ShowFieldGrid>
            </Col>
            <Col xs={24} lg={12}>
              <ShowFieldGrid columns={1}>
                <ShowField label="Reserve 1 Permit" value={record?.reserve1Permit} type="code" copyable />
                <ShowField label="Reserve 1 Version" value={record?.reserve1PermitVersion} />
                <ShowField label="Reserve 2 Permit" value={record?.reserve2Permit} type="code" copyable />
                <ShowField label="Reserve 2 Version" value={record?.reserve2PermitVersion} />
                <ShowField label="Reserve 3 Permit" value={record?.reserve3Permit} type="code" copyable />
                <ShowField label="Reserve 3 Version" value={record?.reserve3PermitVersion} />
              </ShowFieldGrid>
            </Col>
          </Row>
        </ShowSection>
      </Space>

      {/* Subscription Modal */}
      <ShowModal
        title="Associated Subscription"
        subtitle={record?.subscription?.subscriptionName}
        icon={<AppstoreOutlined />}
        open={modalPropsSubscription.open ?? false}
        onClose={closeSubscription}
        empty={!record?.subscription}
        emptyText="No subscription associated"
        width={700}
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="UKHO Licence" value={record?.subscription?.licence?.ukhoLicenceId} type="code" copyable />
          <ShowField label="Subscription Name" value={record?.subscription?.subscriptionName} />
          <ShowField label="Service Type" value={record?.subscription?.serviceType} type="tag" tagColor="purple" />
          <ShowField
            label="Enrollment Date"
            value={record?.subscription?.enrollmentDate ? dayjs(record.subscription.enrollmentDate).format("DD-MMM-YYYY") : undefined}
          />
          <ShowField label="Licence Type" value={record?.subscription?.licenceType} type="status" />
          <ShowField label="Activation Status" value={record?.subscription?.activationStatus} type="status" />
          <ShowField label="Hold Status" value={record?.subscription?.holdStatus} type="status" />
          <ShowField label="Order Value Threshold" value={record?.subscription?.orderValueThreshold} type="currency" />
          <ShowField label="Automated Ordering" value={record?.subscription?.automatedOrdering} type="status" />
          <ShowField label="Automated Permit Issuance" value={record?.subscription?.automatedPermitIssuance} type="status" />
        </ShowFieldGrid>
      </ShowModal>
    </Show>
  );
};
